import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Divider,
  InputLabel,
  ListSubheader,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  type SxProps,
  Tooltip,
} from '@mui/material'

import { type DropdownMaxHeight, colors, dropdownMaxHeight } from '~tokens/internal'

import Icon from '../Icon'
import Search from '../Search'
import Typography from '../Typography'

import './Dropdown.scss'

const SEARCH_THRESHOLD = 10
const LABEL_MAX_CHARS = 72

// --- Types ---

export interface DropdownOption {
  value: string
  label: string
  avatar?: React.ReactNode
  chip?: React.ReactNode
  group?: string
  detailed_information?: React.ReactNode
  subPanel?: React.ReactNode
  subPanelClassName?: string
  subOptions?: DropdownOption[]
  disabled?: boolean
  tooltip?: string
}

export type DropdownVariant = 'default' | 'rounded' | 'text'

export interface DropdownProps {
  options: DropdownOption[]
  value?: string | string[]
  onChange?: (value: string | string[]) => void
  label?: string
  placeholder?: string
  helperText?: string
  errorText?: string
  error?: boolean
  disabled?: boolean
  multiple?: boolean
  searchable?: boolean
  searchThreshold?: number
  searchPlaceholder?: string
  maxVisibleOptions?: number
  suggestedOptions?: DropdownOption[]
  suggestedLabel?: string
  maxHeight?: DropdownMaxHeight
  id?: string
  fullWidth?: boolean
  containerSx?: SxProps
  variant?: DropdownVariant
  required?: boolean
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  renderValue?: (option: DropdownOption) => React.ReactNode
  renderTrigger?: () => React.ReactNode
  onSearchChange?: (query: string) => void
  searchValue?: string
  loading?: boolean
  footerAction?: {
    label: string
    icon?: React.ReactNode
    onClick: () => void
    disabled?: boolean
  }
  showSelectedTooltip?: boolean
  closeOnSelect?: boolean
  /** Lets a single-select dropdown be emptied by picking its selected option again. */
  allowDeselect?: boolean
  /** Fixes the menu's width instead of letting the options size it. */
  menuWidth?: number | string
}

// --- Helpers ---

const cx = (...names: Array<string | false | null | undefined>): string =>
  names.filter(Boolean).join(' ')

// --- Component ---

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select...',
  helperText,
  errorText,
  error = false,
  disabled = false,
  multiple = false,
  searchable,
  searchThreshold = SEARCH_THRESHOLD,
  searchPlaceholder = 'Search...',
  maxVisibleOptions,
  suggestedOptions,
  suggestedLabel = 'Suggested',
  maxHeight = 'lg',
  id,
  fullWidth = true,
  containerSx,
  variant = 'default',
  required = false,
  startAdornment,
  endAdornment,
  renderValue,
  renderTrigger,
  onSearchChange,
  searchValue,
  loading = false,
  footerAction,
  showSelectedTooltip = false,
  closeOnSelect = false,
  allowDeselect = false,
  menuWidth,
}) => {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const triggerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [hoveredOptionValue, setHoveredOptionValue] = useState<string | null>(null)
  const menuPaperRef = useRef<HTMLDivElement | null>(null)
  const displayValueRef = useRef<HTMLDivElement | null>(null)
  const [valueTruncated, setValueTruncated] = useState(false)

  const measureValueTruncation = useCallback(() => {
    const valueElement = displayValueRef.current?.querySelector<HTMLElement>(
      '.internal-dropdown__value',
    )
    setValueTruncated(!!valueElement && valueElement.scrollWidth > valueElement.clientWidth)
  }, [])

  const showSearch = searchable ?? options.length >= searchThreshold

  const currentSearch = onSearchChange ? (searchValue ?? '') : searchQuery

  useEffect(() => {
    if (!open || !showSearch) return

    const frameId = requestAnimationFrame(() => searchInputRef.current?.focus())
    return (): void => cancelAnimationFrame(frameId)
  }, [open, showSearch])

  const filteredOptions = useMemo(() => {
    const query = currentSearch.toLowerCase()
    const matches =
      onSearchChange || !currentSearch
        ? options
        : options.filter(
            (opt) =>
              opt.label.toLowerCase().includes(query) || !!opt.group?.toLowerCase().includes(query),
          )
    if (!maxVisibleOptions) return matches
    const visible = matches.slice(0, maxVisibleOptions)
    const selected = options.find((opt) => opt.value === value)
    return selected && !visible.includes(selected) ? [selected, ...visible] : visible
  }, [options, currentSearch, onSearchChange, maxVisibleOptions, value])

  const filteredSuggested = useMemo(() => {
    if (!suggestedOptions) return []
    if (!currentSearch) return suggestedOptions.slice(0, 3)
    return suggestedOptions
      .filter((opt) => opt.label.toLowerCase().includes(currentSearch.toLowerCase()))
      .slice(0, 3)
  }, [suggestedOptions, currentSearch])

  const handleOpen = useCallback(() => {
    if (!disabled) setOpen(true)
  }, [disabled])

  const handleClose = useCallback(() => {
    setOpen(false)
    setSearchQuery('')
    setHoveredOptionValue(null)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    },
    [disabled],
  )

  const handleSelect = useCallback(
    (optionValue: string) => {
      if (!onChange) return
      if (multiple) {
        const currentValues = (Array.isArray(value) ? value : []) as string[]
        const wasSelected = currentValues.includes(optionValue)
        const newValues = wasSelected
          ? currentValues.filter((v) => v !== optionValue)
          : [...currentValues, optionValue]
        onChange(newValues)
        if (closeOnSelect && !wasSelected) handleClose()
      } else {
        const opt = [...(suggestedOptions ?? []), ...options].find((o) => o.value === optionValue)
        onChange(allowDeselect && value === optionValue ? '' : optionValue)
        if (!opt?.subPanel) handleClose()
      }
    },
    [
      multiple,
      value,
      onChange,
      handleClose,
      options,
      suggestedOptions,
      closeOnSelect,
      allowDeselect,
    ],
  )

  const isSelected = useCallback(
    (optionValue: string): boolean => {
      if (multiple) {
        return Array.isArray(value) && value.includes(optionValue)
      }
      return value === optionValue
    },
    [multiple, value],
  )

  const displayValue = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null
    const allOptions = [...(suggestedOptions || []), ...options]
    if (multiple && Array.isArray(value)) {
      return value
        .map((v) => allOptions.find((o) => o.value === v))
        .filter(Boolean) as DropdownOption[]
    }
    return allOptions.find((o) => o.value === value) || null
  }, [value, multiple, suggestedOptions, options])

  const resolvedMaxHeight = dropdownMaxHeight[maxHeight]

  const isRounded = variant === 'rounded'
  const valueClass = cx(
    'internal-dropdown__value',
    isRounded && 'internal-dropdown__value--rounded',
  )
  const renderSingleValue = (opt: DropdownOption): React.ReactNode => {
    if (opt.chip) return opt.chip
    if (opt.avatar) {
      return (
        <Box className='internal-dropdown__option-content'>
          {opt.avatar}
          <Typography truncate maxChars={LABEL_MAX_CHARS} className={valueClass}>
            {opt.label}
          </Typography>
        </Box>
      )
    }
    return (
      <Typography truncate maxChars={LABEL_MAX_CHARS} className={valueClass}>
        {opt.label}
      </Typography>
    )
  }

  const renderDisplayValue = (): React.ReactNode => {
    if (renderTrigger) return renderTrigger()
    if (!displayValue) {
      return (
        <Typography
          className={cx(
            'internal-dropdown__placeholder',
            isRounded && 'internal-dropdown__placeholder--rounded',
          )}
        >
          {placeholder}
        </Typography>
      )
    }

    if (multiple && Array.isArray(displayValue)) {
      return (
        <Box className='internal-dropdown__multi-value'>
          {(displayValue as DropdownOption[]).map((opt, index) => (
            <React.Fragment key={opt.value}>
              {renderValue
                ? renderValue(opt)
                : opt.chip
                  ? React.isValidElement(opt.chip)
                    ? React.cloneElement(
                        opt.chip as React.ReactElement<{ onDelete?: () => void }>,
                        { onDelete: () => handleSelect(opt.value) },
                      )
                    : opt.chip
                  : renderSingleValue(opt)}
              {!renderValue &&
                !opt.chip &&
                index < (displayValue as DropdownOption[]).length - 1 && (
                  <Typography className={cx(valueClass, 'internal-dropdown__value--comma')}>
                    ,
                  </Typography>
                )}
            </React.Fragment>
          ))}
        </Box>
      )
    }

    if (renderValue) return renderValue(displayValue as DropdownOption)
    return renderSingleValue(displayValue as DropdownOption)
  }

  const renderDetailedOption = (opt: DropdownOption): React.ReactNode => (
    <Box className='internal-dropdown__option-text'>
      <Box className='internal-dropdown__option-title-row'>
        {opt.avatar}
        <Typography truncate maxChars={LABEL_MAX_CHARS} component='p' variant='body1'>
          {opt.label}
        </Typography>
      </Box>
      <Typography
        component='div'
        variant='body2'
        sx={{
          color: 'text.secondary',
        }}
      >
        {opt.detailed_information}
      </Typography>
    </Box>
  )

  const renderOption = (opt: DropdownOption, isLast: boolean = false): React.ReactNode => {
    const menuItem = (
      <MenuItem
        onClick={() => !opt.disabled && !opt.subOptions && handleSelect(opt.value)}
        selected={isSelected(opt.value)}
        disabled={opt.disabled}
        role='menuitemcheckbox'
        aria-checked={isSelected(opt.value)}
        onMouseEnter={
          opt.subPanel || opt.subOptions
            ? (): void => setHoveredOptionValue(opt.value)
            : (): void => setHoveredOptionValue(null)
        }
      >
        <Box className='internal-dropdown__option-row'>
          <Box className='internal-dropdown__option-content'>
            {opt.chip ? (
              <>
                {opt.avatar}
                {opt.chip}
              </>
            ) : opt.detailed_information ? (
              renderDetailedOption(opt)
            ) : (
              <>
                {opt.avatar}
                <Typography truncate maxChars={LABEL_MAX_CHARS} variant='body2'>
                  {opt.label}
                </Typography>
              </>
            )}
          </Box>
          {isSelected(opt.value) && (
            <Icon name='Check' size='1rem' className='internal-dropdown__check' />
          )}
          {opt.subOptions && (
            <Icon name='chevronRight' size='1rem' className='internal-dropdown__chevron' />
          )}
        </Box>
      </MenuItem>
    )
    return (
      <React.Fragment key={opt.value}>
        {opt.tooltip ? (
          <Tooltip title={opt.tooltip} placement='right'>
            {opt.disabled ? <span>{menuItem}</span> : menuItem}
          </Tooltip>
        ) : (
          menuItem
        )}
        {!isLast && !!opt.detailed_information && <Divider />}
      </React.Fragment>
    )
  }

  const hasGroups = filteredOptions.some((opt) => opt.group)

  const groupedFilteredOptions = useMemo(() => {
    if (!hasGroups) return null
    const groups: { label: string; options: DropdownOption[] }[] = []
    const groupMap = new Map<string, DropdownOption[]>()
    const ungrouped: DropdownOption[] = []
    for (const opt of filteredOptions) {
      if (opt.group) {
        if (!groupMap.has(opt.group)) {
          const arr: DropdownOption[] = []
          groupMap.set(opt.group, arr)
          groups.push({ label: opt.group, options: arr })
        }
        groupMap.get(opt.group)!.push(opt)
      } else {
        ungrouped.push(opt)
      }
    }
    if (ungrouped.length > 0) {
      groups.unshift({ label: '', options: ungrouped })
    }
    return groups
  }, [filteredOptions, hasGroups])

  const renderGroupedOptions = (): React.ReactNode => {
    if (!groupedFilteredOptions) return null
    return groupedFilteredOptions.map((group, gi) => (
      <React.Fragment key={group.label || `ungrouped-${gi}`}>
        {group.label && (
          <ListSubheader className='internal-dropdown__group-subheader'>
            {group.label}
          </ListSubheader>
        )}
        {group.options.map((opt, idx) => renderOption(opt, idx === group.options.length - 1))}
      </React.Fragment>
    ))
  }

  const triggerClass = cx(
    'internal-dropdown__trigger',
    `internal-dropdown__trigger--${variant}`,
    error && 'internal-dropdown__trigger--error',
    open && 'internal-dropdown__trigger--open',
    disabled && 'internal-dropdown__trigger--disabled',
  )

  const hoveredOption = options.find((o) => o.value === hoveredOptionValue)

  const selectedOptionTooltip =
    showSelectedTooltip && displayValue && !Array.isArray(displayValue)
      ? (displayValue as DropdownOption).tooltip
      : null

  // A value the trigger is too narrow to spell out is worth a tooltip; one it
  // shows in full is not. Labels past LABEL_MAX_CHARS already carry the
  // Typography truncation tooltip.
  const truncatedValueLabel =
    valueTruncated && displayValue && !Array.isArray(displayValue)
      ? (displayValue as DropdownOption).label
      : null
  const truncatedValueTooltip =
    truncatedValueLabel && truncatedValueLabel.length <= LABEL_MAX_CHARS
      ? truncatedValueLabel
      : null

  return (
    <Box
      className={cx(
        'internal-dropdown',
        fullWidth ? 'internal-dropdown--width-full' : 'internal-dropdown--width-fit',
      )}
      sx={containerSx}
    >
      {label && (
        <InputLabel
          htmlFor={id}
          shrink
          className={cx(
            'internal-dropdown__label',
            disabled ? 'internal-dropdown__label--disabled' : 'internal-dropdown__label--secondary',
          )}
        >
          {label}
          {required && (
            <Typography component='span' className='internal-dropdown__required'>
              *
            </Typography>
          )}
        </InputLabel>
      )}
      <Box className='internal-dropdown__trigger-row'>
        <Tooltip
          title={selectedOptionTooltip ?? truncatedValueTooltip ?? ''}
          placement='bottom-start'
          disableHoverListener={open}
        >
          <Box
            ref={triggerRef}
            onMouseEnter={measureValueTruncation}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            role='button'
            aria-haspopup='listbox'
            aria-expanded={open}
            tabIndex={disabled ? -1 : 0}
            id={id}
            className={triggerClass}
          >
            {startAdornment && <Box className='internal-dropdown__adornment'>{startAdornment}</Box>}
            <Box ref={displayValueRef} className='internal-dropdown__display-value'>
              {renderDisplayValue()}
            </Box>
            {endAdornment && <Box className='internal-dropdown__adornment'>{endAdornment}</Box>}
            {variant !== 'text' && (
              <Icon
                name={open ? 'ArrowDropUp' : 'ArrowDropDown'}
                color={disabled ? colors.neutral[500] : colors.neutral[600]}
              />
            )}
          </Box>
        </Tooltip>
      </Box>
      {(helperText || errorText) && (
        <Typography
          className={cx(
            'internal-dropdown__helper-text',
            error && errorText && 'internal-dropdown__helper-text--error',
          )}
        >
          {error && errorText ? errorText : helperText}
        </Typography>
      )}
      <Menu
        open={open}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            ref: menuPaperRef,
            className: 'internal-dropdown__paper',
            sx: {
              minWidth: menuWidth ?? triggerRef.current?.offsetWidth,
              width: menuWidth,
              maxHeight: resolvedMaxHeight,
            },
          },
        }}
      >
        {showSearch && (
          <Box className='internal-dropdown__search' onKeyDown={(e) => e.stopPropagation()}>
            <Search
              autoFocus
              inputRef={searchInputRef}
              placeholder={searchPlaceholder}
              value={currentSearch}
              onChange={(e) =>
                onSearchChange ? onSearchChange(e.target.value) : setSearchQuery(e.target.value)
              }
              onClear={() => (onSearchChange ? onSearchChange('') : setSearchQuery(''))}
            />
          </Box>
        )}
        <Box className='internal-dropdown__menu'>
          {loading && (
            <MenuItem disabled>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Searching...
              </Typography>
            </MenuItem>
          )}
          {filteredSuggested.length > 0 && (
            <>
              <ListSubheader
                className={cx(
                  'internal-dropdown__group-subheader',
                  'internal-dropdown__group-subheader--suggested',
                )}
              >
                {suggestedLabel}
              </ListSubheader>
              {filteredSuggested.map((opt, idx) =>
                renderOption(opt, idx === filteredSuggested.length - 1),
              )}
            </>
          )}
          {!(onSearchChange && loading && currentSearch.trim().length > 0) &&
            (hasGroups
              ? renderGroupedOptions()
              : filteredOptions.map((opt, idx) =>
                  renderOption(opt, idx === filteredOptions.length - 1),
                ))}
          {filteredOptions.length === 0 &&
            !loading &&
            (!showSearch || currentSearch.trim().length > 0) && (
              <MenuItem disabled>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No results found
                </Typography>
              </MenuItem>
            )}
        </Box>
        {footerAction && (
          <Box className='internal-dropdown__footer'>
            <Divider />
            <MenuItem
              className='internal-dropdown__footer-action'
              disabled={footerAction.disabled}
              onClick={() => {
                footerAction.onClick()
                handleClose()
              }}
            >
              <Box className='internal-dropdown__option-content'>
                {footerAction.icon}
                <Typography variant='body2'>{footerAction.label}</Typography>
              </Box>
            </MenuItem>
          </Box>
        )}
      </Menu>
      <Popper
        open={
          open &&
          Boolean(menuPaperRef.current) &&
          Boolean(hoveredOption?.subPanel || hoveredOption?.subOptions)
        }
        anchorEl={menuPaperRef.current}
        placement='right-start'
        style={{ zIndex: 1300 }}
        modifiers={[
          { name: 'flip', enabled: true },
          {
            name: 'preventOverflow',
            enabled: true,
            options: { boundary: 'viewport', padding: 100 },
          },
        ]}
      >
        <Paper className={cx('filter-paper', hoveredOption?.subPanelClassName)}>
          {hoveredOption?.subPanel ??
            (hoveredOption?.subOptions && (
              <MenuList disablePadding>
                {hoveredOption.subOptions.map((sub) => (
                  <MenuItem
                    key={sub.value}
                    className='filter-menu-item'
                    onClick={() => handleSelect(sub.value)}
                    selected={isSelected(sub.value)}
                  >
                    <Box className='internal-dropdown__option-row'>
                      <Typography variant='body2'>{sub.label}</Typography>
                      {isSelected(sub.value) && (
                        <Icon name='Check' size='1rem' className='internal-dropdown__check' />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </MenuList>
            ))}
        </Paper>
      </Popper>
    </Box>
  )
}

export default Dropdown
